import { findLayoutPort, layoutPortKey } from './portLayout';

const STORAGE_KEY = 'netprobuilder-port-naming-schemes';

export const NUMBERS_ONLY_SCHEME_ID = 'numbers';
export const MIKROTIK_SCHEME_ID = 'mikrotik';

export type PortNamingPrefixes = Record<string, string>;

export interface PortNamingScheme {
  id: string;
  name: string;
  prefixes: PortNamingPrefixes;
  builtIn?: boolean;
  createdAt?: number;
}

export const BUILT_IN_PORT_NAMING_SCHEMES: PortNamingScheme[] = [
  {
    id: NUMBERS_ONLY_SCHEME_ID,
    name: 'Numbers only',
    prefixes: {},
    builtIn: true,
  },
  {
    id: MIKROTIK_SCHEME_ID,
    name: 'MikroTik',
    prefixes: {
      rj45: 'ether',
      sfp: 'sfp',
      sfpPlus: 'sfpplus',
    },
    builtIn: true,
  },
];

function emptyCustomStore(): Record<string, PortNamingScheme> {
  return {};
}

export function loadCustomPortNamingSchemes(): Record<string, PortNamingScheme> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyCustomStore();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return emptyCustomStore();
    const out: Record<string, PortNamingScheme> = {};
    Object.entries(parsed).forEach(([id, value]) => {
      if (!value || typeof value !== 'object') return;
      const v = value as Partial<PortNamingScheme>;
      if (typeof v.name !== 'string' || !v.name.trim()) return;
      out[id] = {
        id,
        name: v.name.trim(),
        prefixes: v.prefixes && typeof v.prefixes === 'object' ? { ...v.prefixes } : {},
        createdAt: typeof v.createdAt === 'number' ? v.createdAt : Date.now(),
      };
    });
    return out;
  } catch {
    return emptyCustomStore();
  }
}

export function saveCustomPortNamingSchemes(schemes: Record<string, PortNamingScheme>): void {
  try {
    const toStore: Record<string, PortNamingScheme> = {};
    Object.entries(schemes).forEach(([id, scheme]) => {
      if (scheme.builtIn) return;
      toStore[id] = {
        id,
        name: scheme.name,
        prefixes: scheme.prefixes || {},
        createdAt: scheme.createdAt,
      };
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  } catch (error) {
    console.error('Failed to save port naming schemes:', error);
  }
}

export function allPortNamingSchemes(
  custom: Record<string, PortNamingScheme> = loadCustomPortNamingSchemes()
): PortNamingScheme[] {
  return [...BUILT_IN_PORT_NAMING_SCHEMES, ...Object.values(custom)];
}

export function getPortNamingScheme(
  id: string | null | undefined,
  custom: Record<string, PortNamingScheme> = loadCustomPortNamingSchemes()
): PortNamingScheme {
  const schemes = allPortNamingSchemes(custom);
  return schemes.find((s) => s.id === id) || BUILT_IN_PORT_NAMING_SCHEMES[0];
}

export function prefixForPortKind(scheme: PortNamingScheme | null | undefined, kind: string): string {
  if (!scheme) return '';
  if (kind.startsWith('custom:')) {
    return (scheme.prefixes.custom || '').trim();
  }
  return (scheme.prefixes[kind] || '').trim();
}

export function formatPortLabel(
  scheme: PortNamingScheme | null | undefined,
  kind: string,
  number: number
): string {
  const prefix = prefixForPortKind(scheme, kind);
  return prefix ? `${prefix}${number}` : String(number);
}

export function nextUnusedPortNumber(numbers: Array<number | undefined | null>): number {
  const used = new Set(numbers.filter((n): n is number => typeof n === 'number' && n >= 1));
  let n = 1;
  while (used.has(n)) n += 1;
  return n;
}

/** Port numbers are unique per port type (RJ45 1..n and SFP 1..n can both exist). */
export function fillMissingPortNumbers<T extends { type: string; number?: number }>(cells: T[]): T[] {
  const usedByType = new Map<string, Set<number>>();

  const usedSetFor = (type: string) => {
    let set = usedByType.get(type);
    if (!set) {
      set = new Set<number>();
      usedByType.set(type, set);
    }
    return set;
  };

  const prepared = cells.map((cell) => {
    if (cell.type === 'empty' || cell.type === 'blank') return { ...cell, number: undefined };
    const used = usedSetFor(cell.type);
    if (typeof cell.number === 'number' && cell.number >= 1 && !used.has(cell.number)) {
      used.add(cell.number);
      return cell;
    }
    return { ...cell, number: undefined };
  });

  return prepared.map((cell) => {
    if (cell.type === 'empty' || cell.type === 'blank' || typeof cell.number === 'number') return cell;
    const used = usedSetFor(cell.type);
    const number = nextUnusedPortNumber(Array.from(used));
    used.add(number);
    return { ...cell, number };
  });
}

export type PortEndpointParts = {
  equipmentId: string;
  number: number;
  kind?: string;
  face?: string;
};

/** `eq:port:N`, `eq:port:N:kind:KIND`, optional `:face:FACE`. */
export function buildPortEndpointId(
  equipmentId: string,
  number: number,
  options?: { kind?: string | null; face?: string | null }
): string {
  let id = `${equipmentId}:port:${number}`;
  if (options?.kind) {
    id += `:kind:${encodeURIComponent(String(options.kind))}`;
  }
  if (options?.face) {
    id += `:face:${options.face}`;
  }
  return id;
}

export function parsePortEndpointId(endpoint: string): PortEndpointParts | null {
  if (typeof endpoint !== 'string' || !endpoint.includes(':port:')) return null;
  const m = endpoint.match(/^(.*):port:(\d+)(?::kind:([^:]+))?(?::face:(.+))?$/);
  if (!m) return null;
  const number = parseInt(m[2], 10);
  if (!Number.isFinite(number)) return null;
  return {
    equipmentId: m[1],
    number,
    kind: m[3] ? decodeURIComponent(m[3]) : undefined,
    face: m[4] || undefined,
  };
}

export function endpointMatchesPort(
  endpoint: string,
  equipmentId: string,
  number: number,
  kind?: string | null
): boolean {
  const parsed = parsePortEndpointId(endpoint);
  if (!parsed || parsed.equipmentId !== equipmentId || parsed.number !== number) return false;
  if (parsed.kind && kind) return parsed.kind === kind;
  return true;
}

/**
 * Match a cable endpoint to one layout port. Same numbers on RJ45 / SFP / custom
 * are distinct; a kind-less legacy cable binds to only one of them.
 */
export function endpointMatchesLayoutPort(
  endpoint: string,
  equipmentId: string,
  port: { id?: string; number: number; kind?: string | null },
  siblingPorts: Array<{ id?: string; number: number; kind?: string | null }>
): boolean {
  const parsed = parsePortEndpointId(endpoint);
  if (!parsed || parsed.equipmentId !== equipmentId || parsed.number !== port.number) return false;
  const portKind = port.kind != null && String(port.kind) !== '' ? String(port.kind) : undefined;
  if (parsed.kind) return parsed.kind === portKind;
  const unique = findLayoutPort(siblingPorts, parsed.number, parsed.kind);
  if (!unique) return false;
  return layoutPortKey(unique) === layoutPortKey(port);
}

export type PortNumberRemap = {
  face?: string;
  /** Legacy number→number map (custom labels, simple rewrites). */
  map: Record<number, number>;
  /** Full `:port:` suffix remaps, e.g. `8` → `1:kind:sfp`. */
  endpoints?: Record<string, string>;
};

function portEndpointSuffix(port: { number: number; kind?: string }, face?: string): string {
  let suffix = String(port.number);
  if (port.kind) suffix += `:kind:${encodeURIComponent(String(port.kind))}`;
  if (face) suffix += `:face:${face}`;
  return suffix;
}

function portMatchKey(port: { number?: number; kind?: string; x?: number; y?: number }): string {
  return `${port.number ?? ''}|${port.kind != null ? String(port.kind) : ''}|${port.x ?? ''}|${port.y ?? ''}`;
}

function collectFacedPortsFromLayout(layout: any): Array<{ number: number; kind?: string; face?: string }> {
  const out: Array<{ number: number; kind?: string; face?: string }> = [];
  if (!layout) return out;
  const multi = layout.multiFace;
  if (multi?.faces) {
    Object.entries(multi.faces).forEach(([face, faceLayout]: [string, any]) => {
      (faceLayout?.ports || []).forEach((p: any) => {
        if (typeof p?.number === 'number') {
          out.push({
            number: p.number,
            kind: p.kind != null ? String(p.kind) : undefined,
            face,
          });
        }
      });
    });
  }
  if (out.length === 0) {
    (layout.simple?.ports || []).forEach((p: any) => {
      if (typeof p?.number === 'number') {
        out.push({
          number: p.number,
          kind: p.kind != null ? String(p.kind) : undefined,
        });
      }
    });
  }
  return out;
}

/** True when the endpoint still points at a real port on the layout. */
export function endpointExistsOnLayout(endpoint: string, layout: any): boolean {
  const parsed = parsePortEndpointId(endpoint);
  if (!parsed) return false;
  const ports = collectFacedPortsFromLayout(layout);
  if (ports.length === 0) return false;
  return ports.some((p) => {
    if (p.number !== parsed.number) return false;
    if (parsed.face && p.face && parsed.face !== p.face) return false;
    if (parsed.kind) return p.kind === parsed.kind;
    return true;
  });
}

/**
 * Match old→new ports for cable rewrites:
 * 1) same cell (x,y)
 * 2) same kind + number
 * 3) unique unused kind
 * Unmatched ports are left out so callers can detach those cables to the badge.
 */
export function buildPortNumberRemaps(
  prevLayout: any,
  nextLayout: any
): PortNumberRemap[] {
  const remaps: PortNumberRemap[] = [];

  const mapPorts = (oldPorts: any[] | undefined, newPorts: any[] | undefined, face?: string) => {
    if (!Array.isArray(oldPorts) || !Array.isArray(newPorts)) return;
    const map: Record<number, number> = {};
    const endpoints: Record<string, string> = {};
    const usedNew = new Set<string>();

    const claim = (port: any) => {
      usedNew.add(portMatchKey(port));
    };

    const findMatch = (oldPort: any): any | undefined => {
      const byPos = newPorts.find(
        (p) =>
          p &&
          typeof p.number === 'number' &&
          p.x === oldPort.x &&
          p.y === oldPort.y &&
          !usedNew.has(portMatchKey(p))
      );
      if (byPos) return byPos;

      const oldKind = oldPort.kind != null ? String(oldPort.kind) : undefined;
      if (oldKind) {
        const byKindNumber = newPorts.find(
          (p) =>
            p &&
            typeof p.number === 'number' &&
            p.number === oldPort.number &&
            String(p.kind) === oldKind &&
            !usedNew.has(portMatchKey(p))
        );
        if (byKindNumber) return byKindNumber;

        const sameKind = newPorts.filter(
          (p) =>
            p &&
            typeof p.number === 'number' &&
            String(p.kind) === oldKind &&
            !usedNew.has(portMatchKey(p))
        );
        if (sameKind.length === 1) return sameKind[0];
      } else {
        const byNumber = newPorts.filter(
          (p) =>
            p &&
            typeof p.number === 'number' &&
            p.number === oldPort.number &&
            !usedNew.has(portMatchKey(p))
        );
        if (byNumber.length === 1) return byNumber[0];
      }
      return undefined;
    };

    oldPorts.forEach((oldPort) => {
      if (typeof oldPort?.number !== 'number') return;
      const match = findMatch(oldPort);
      if (!match) return;
      claim(match);

      const oldKind = oldPort.kind != null ? String(oldPort.kind) : undefined;
      const newKind = match.kind != null ? String(match.kind) : undefined;
      const numberChanged = match.number !== oldPort.number;
      const kindChanged = oldKind !== newKind;
      if (!numberChanged && !kindChanged) return;

      if (numberChanged) {
        map[oldPort.number] = match.number;
      }
      const newSuffix = portEndpointSuffix(
        { number: match.number, kind: newKind },
        face
      );
      endpoints[portEndpointSuffix({ number: oldPort.number }, face)] = newSuffix;
      if (oldKind) {
        endpoints[portEndpointSuffix({ number: oldPort.number, kind: oldKind }, face)] = newSuffix;
      }
    });
    if (Object.keys(map).length > 0 || Object.keys(endpoints).length > 0) {
      remaps.push({ face, map, endpoints });
    }
  };

  const prevHasMulti = !!(prevLayout?.multiFace?.faces && Object.keys(prevLayout.multiFace.faces).length);
  const nextHasMulti = !!(nextLayout?.multiFace?.faces && Object.keys(nextLayout.multiFace.faces).length);

  if (prevHasMulti || nextHasMulti) {
    const oldFaces = prevLayout?.multiFace?.faces || {};
    const newFaces = nextLayout?.multiFace?.faces || {};
    // simple → multiFace front (or reverse)
    if (!prevHasMulti && prevLayout?.simple?.ports) {
      mapPorts(prevLayout.simple.ports, newFaces.front?.ports || nextLayout?.simple?.ports, 'front');
    } else if (!nextHasMulti && nextLayout?.simple?.ports) {
      mapPorts(oldFaces.front?.ports || prevLayout?.simple?.ports, nextLayout.simple.ports, 'front');
    } else {
      const faceKeys = new Set([...Object.keys(oldFaces), ...Object.keys(newFaces)]);
      faceKeys.forEach((face) => {
        mapPorts(oldFaces[face]?.ports, newFaces[face]?.ports, face);
      });
    }
  } else if (prevLayout?.simple || nextLayout?.simple) {
    mapPorts(prevLayout?.simple?.ports, nextLayout?.simple?.ports);
  }

  return remaps;
}

export function rewritePortEndpoint(
  endpoint: string,
  placedId: string,
  remaps: PortNumberRemap[],
  options?: { nextLayout?: any; detachOrphans?: boolean }
): string {
  const prefix = `${placedId}:port:`;
  if (!endpoint.startsWith(prefix)) return endpoint;

  const rest = endpoint.slice(prefix.length);
  const parsed = parsePortEndpointId(endpoint);
  const face = parsed?.face;

  const remap = face
    ? remaps.find((r) => r.face === face) || remaps.find((r) => !r.face)
    : remaps.find((r) => !r.face) || remaps[0];

  let next = endpoint;
  if (remap) {
    if (remap.endpoints && remap.endpoints[rest]) {
      next = `${prefix}${remap.endpoints[rest]}`;
    } else if (parsed) {
      const mappedNumber = remap.map[parsed.number];
      if (mappedNumber != null && mappedNumber !== parsed.number) {
        next = buildPortEndpointId(placedId, mappedNumber, { kind: parsed.kind, face: parsed.face });
      }
    }
  }

  if (options?.detachOrphans && options.nextLayout) {
    if (!endpointExistsOnLayout(next, options.nextLayout)) {
      return placedId;
    }
  }

  return next;
}
